/** Tenant-detail Revenue tab: one tenant's totals + line-kind breakdown. */
import { useEffect, useMemo, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState, ErrorState } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import type { RevenueReport } from '@/api/tmm/types';
import { periodFor, type PeriodKey } from '../dashboard/period';
import { RevenueTotals } from '../revenue/RevenueTotals';
import { LineKindBreakdown } from '../revenue/LineKindBreakdown';

interface TenantRevenuePanelProps {
  slug: string;
  currency: string;
}

const PERIOD_KEYS: PeriodKey[] = ['THIS_MONTH', 'LAST_30_DAYS', 'LAST_MONTH'];

export function TenantRevenuePanel({ slug, currency }: TenantRevenuePanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [periodKey, setPeriodKey] = useState<PeriodKey>('THIS_MONTH');
  const period = useMemo(() => periodFor(periodKey), [periodKey]);
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<{
    key: string;
    report: RevenueReport | null;
    error: boolean;
    failure: unknown;
  }>({ key: '', report: null, error: false, failure: null });
  const key = `${slug}:${period.from}:${period.to}:${reloadToken}`;

  useEffect(() => {
    let cancelled = false;
    adminApi
      .tenantRevenue(slug, { from: period.from, to: period.to })
      .then((body) => {
        if (cancelled) return;
        setState({ key, report: body, error: false, failure: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ key, report: null, error: true, failure: err });
      });
    return () => {
      cancelled = true;
    };
  }, [key, slug, period.from, period.to]);

  const current = state.key === key;
  const report = current ? state.report : null;
  const loading = !current;
  const error = current && state.error;
  const breakdown = report?.tenants[0]?.byLineKind ?? [];
  const empty = report !== null && report.tenants.length === 0;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { sm: 'flex-end' }, justifyContent: 'space-between', mb: 2 }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="tenant-revenue-period-label">
            {t('dashboard.period.label', 'Period')}
          </InputLabel>
          <Select
            labelId="tenant-revenue-period-label"
            value={periodKey}
            label={t('dashboard.period.label', 'Period')}
            onChange={(event) => setPeriodKey(event.target.value as PeriodKey)}
          >
            {PERIOD_KEYS.map((key) => (
              <MenuItem key={key} value={key}>
                {t(`dashboard.period.${key}`, key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error ? (
        <Box sx={{ mb: 2 }}>
          <ErrorState
            error={state.failure}
            message={t('revenue.loadError', 'Could not load revenue.')}
            onRetry={() => setReloadToken((n) => n + 1)}
          />
        </Box>
      ) : null}

      <RevenueTotals totals={report?.totals ?? null} locale={i18n.language} loading={loading} />

      {empty ? (
        <EmptyState surface title={t('revenue.empty', 'No revenue in this period.')} />
      ) : null}

      {breakdown.length > 0 ? (
        <Box sx={{ mt: 3 }}>
          <LineKindBreakdown rows={breakdown} currency={currency} locale={i18n.language} />
        </Box>
      ) : null}
    </Box>
  );
}
